import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  DollarSign, 
  Clock, 
  User, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Calendar,
  Wrench
} from 'lucide-react';

const MaintenanceEstimateApproval = ({ requestId, onClose, onApproval }) => {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [activeEstimate, setActiveEstimate] = useState(null);

  useEffect(() => {
    if (requestId) {
      fetchEstimates();
    }
  }, [requestId]);

  const fetchEstimates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/${requestId}/estimates`);
      const data = await response.json();
      setEstimates(data);
      
      // Set active estimate to the first pending one
      const pendingEstimate = data.find(e => e.status === 'pending');
      if (pendingEstimate) {
        setActiveEstimate(pendingEstimate);
      } else if (data.length > 0) {
        setActiveEstimate(data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch estimates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (estimateId, status) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/estimates/${estimateId}/approval`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: status,
          notes: approvalNotes
        })
      });

      if (response.ok) {
        // Refresh estimates
        await fetchEstimates();
        
        // Call parent callback
        if (onApproval) {
          onApproval(estimateId, status);
        }
        
        // Clear notes
        setApprovalNotes('');
        
        // Close if approved/rejected
        if (status !== 'request_revision') {
          onClose?.();
        }
      } else {
        console.error('Failed to update estimate approval');
      }
    } catch (error) {
      console.error('Failed to approve estimate:', error);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending Approval' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
      revised: { color: 'bg-blue-100 text-blue-800', label: 'Revision Requested' }
    };
    
    const variant = variants[status] || variants.pending;
    
    return (
      <Badge className={variant.color}>
        {variant.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading estimates...</p>
        </CardContent>
      </Card>
    );
  }

  if (!activeEstimate) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Wrench className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">No estimates found for this work order.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Estimate Selection */}
      {estimates.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Select Estimate to Review</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {estimates.map((estimate) => (
                <Button
                  key={estimate._id}
                  variant={activeEstimate._id === estimate._id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveEstimate(estimate)}
                  className="flex items-center space-x-2"
                >
                  <DollarSign className="h-4 w-4" />
                  <span>${estimate.total_cost.toLocaleString()}</span>
                  {getStatusBadge(estimate.status)}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Estimate Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Maintenance Estimate
            </CardTitle>
            {getStatusBadge(activeEstimate.status)}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Cost Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                ${activeEstimate.labor_cost.toLocaleString()}
              </div>
              <div className="text-sm text-blue-800">Labor Cost</div>
              <div className="text-xs text-blue-700 mt-1">
                {activeEstimate.estimated_hours} hours estimated
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                ${activeEstimate.materials_cost.toLocaleString()}
              </div>
              <div className="text-sm text-green-800">Materials Cost</div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-800">
                ${activeEstimate.total_cost.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Cost</div>
              <div className="text-xs text-gray-500 mt-1">
                {activeEstimate.total_cost < 400 && (
                  <span className="text-green-600">✓ Under auto-approval threshold</span>
                )}
              </div>
            </div>
          </div>

          {/* Timeline and Contractor Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                Timeline
              </Label>
              <p className="text-sm text-gray-600">
                Estimated {activeEstimate.timeline_days} day{activeEstimate.timeline_days !== 1 ? 's' : ''} to complete
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Submitted
              </Label>
              <p className="text-sm text-gray-600">
                {formatDate(activeEstimate.created_at)}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Work Description</Label>
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-700">
                {activeEstimate.description}
              </p>
            </div>
          </div>

          {/* Auto-approval Notice */}
          {activeEstimate.total_cost < 400 && activeEstimate.status === 'approved' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800 font-medium">
                  This estimate was automatically approved (under $400 threshold)
                </span>
              </div>
            </div>
          )}

          {/* Approval Actions */}
          {activeEstimate.status === 'pending' && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label htmlFor="notes">Approval Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes or special instructions..."
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button 
                  onClick={() => handleApproval(activeEstimate._id, 'approved')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Estimate
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => handleApproval(activeEstimate._id, 'request_revision')}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Request Revision
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => handleApproval(activeEstimate._id, 'rejected')}
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>
              
              {activeEstimate.total_cost >= 400 && (
                <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded-md">
                  💡 This estimate requires manual approval as it's above the $400 auto-approval threshold.
                </p>
              )}
            </div>
          )}

          {/* Previous Approval Info */}
          {activeEstimate.status !== 'pending' && activeEstimate.approval_notes && (
            <div className="space-y-2 pt-4 border-t">
              <Label>Approval Notes</Label>
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700">
                  {activeEstimate.approval_notes}
                </p>
              </div>
              {activeEstimate.approved_at && (
                <p className="text-xs text-gray-500">
                  {activeEstimate.status === 'approved' ? 'Approved' : 'Updated'} on {formatDate(activeEstimate.approved_at)}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MaintenanceEstimateApproval;